import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { todayLocalISO } from "../utils/date";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWandMagicSparkles, faSpinner, faCheck, faRotateLeft, faCommentDots,
  faDumbbell,
  faUser, faBullseye, faCalendarDays, faStar,
  faShieldHalved, faLock, faPenToSquare, faCircleExclamation,
  faBed, faPersonWalking, faPersonRunning, faBolt,
  faArrowTrendUp, faWeightHanging, faFire, faHeartPulse, faBandage,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./SelfWorkoutModal.module.css";
import ModalHeader from "./ModalHeader";
import DayCard from "./DayCard";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import SplitDateInput from "./SplitDateInput";

// ── helpers ───────────────────────────────────────────────────────────────────
// DayCard foi extraído pra ./DayCard.jsx (reaproveitado também pelo
// MigrateWorkoutModal.jsx — ver docs/07-issues.md).

function deriveFitnessLevel(activityLevel) {
  if (activityLevel === "muito_ativo") return "avançado";
  if (activityLevel === "ativo" || activityLevel === "moderado") return "intermediário";
  return "iniciante";
}

// LGPD Art. 14: self-service (sem professor) é restrito a maiores de idade.
// Menores só têm acesso via conta vinculada a um professor.
const MIN_SELF_SERVICE_AGE = 18;

function calculateAge(birthDateStr) {
  if (!birthDateStr) return null;
  const birth = new Date(birthDateStr);
  if (Number.isNaN(birth.getTime())) return null;
  return Math.floor((Date.now() - birth.getTime()) / (365.25 * 86400000));
}


// ── shared input style ────────────────────────────────────────────────────────

const fieldStyle = {
  width: "100%", boxSizing: "border-box",
  background: "var(--surface-secondary)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8, padding: "8px 10px",
  color: "var(--text-primary)", fontSize: 13,
  colorScheme: "dark", outline: "none",
};

// ── constants ─────────────────────────────────────────────────────────────────

const SECONDARY_GOALS = [
  "Melhorar postura",
  "Prevenir lesões",
  "Ganhar flexibilidade",
  "Aumentar energia",
];

// Limites de caracteres para campos livres que alimentam o prompt da IA
const RESTRICTIONS_MAX = 200;
const ABOUT_ME_MAX = 500;

const DEFAULT_FORM = {
  goals: [],
  days_per_week: null,
  session_duration_minutes: null,
  focus: "",
  restrictions: "",
  secondary_goals: [],
  include_cardio: false,
  cardio_type: "",
  cardio_duration_minutes: null,
  weight_kg: "",
  about_me: "",
  birth_date: "",
  gender: "",
  height_cm: "",
  activity_level: "",
  equipment: [],
};

// ── Mensagens rotativas durante geração ───────────────────────────────────────
const GENERATING_MESSAGES = [
  "Analisando seus objetivos…",
  "Selecionando os exercícios ideais…",
  "Montando sua divisão semanal…",
  "Ajustando volumes e cargas…",
  "Personalizando pela sua rotina…",
  "Finalizando sua ficha…",
];

// ── Step meta ─────────────────────────────────────────────────────────────────

const STEP_META = {
  termos:    { icon: faWandMagicSparkles, title: "Vamos montar seu treino.",  sub: "Personalizado para você em minutos.",   name: "Início"         },
  perfil:    { icon: faUser,              title: "Seu perfil",                sub: "Base para calibrar intensidade.",       name: "Perfil"         },
  atividade: { icon: faHeartPulse,        title: "Nível de atividade",        sub: "Como você está hoje?",                  name: "Atividade"      },
  objetivos: { icon: faBullseye,          title: "Seus objetivos",            sub: "O que você quer conquistar?",           name: "Objetivos"      },
  habitos:   { icon: faCalendarDays,      title: "Sua rotina",                sub: "Como você prefere treinar?",            name: "Rotina"         },
  cardio:    { icon: faDumbbell,          title: "Cardio e foco",             sub: "Detalhes do seu treino.",               name: "Detalhes"       },
  personal:  { icon: faStar,             title: "Personalização final",      sub: "Quanto mais detalhes, melhor.",         name: "Personalização" },
};

// ── Chip button ───────────────────────────────────────────────────────────────

function Chip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.chip} ${active ? styles.chipActive : ""}`}
    >
      {label}
    </button>
  );
}

// ── OptionCard ────────────────────────────────────────────────────────────────

function OptionCard({ label, sub, active, onClick, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.optionCard} ${active ? styles.optionCardActive : ""}`}
    >
      {icon && <FontAwesomeIcon icon={icon} className={styles.optionCardIcon} />}
      <span className={styles.optionCardLabel}>{label}</span>
      {sub && <span className={styles.optionCardSub}>{sub}</span>}
    </button>
  );
}

// ── SelfWorkoutModal ──────────────────────────────────────────────────────────

// ── Flag de UX do preview pós-geração ────────────────────────────────────────
// true  = versão SIMPLIFICADA (padrão atual): só o rationale + aviso de ajuda +
//         botão único "Ver meus treinos" (já confirma). Sem regerar, sem rolar
//         a lista inteira — a maioria dos alunos se perdia no excesso de info.
// false = versão COMPLETA (comportamento antigo): rationale + todos os treinos +
//         "Ajustar" (regerar 1x) + "Confirmar e criar treinos".
// Trocar para false pra voltar ao fluxo completo quando o processo amadurecer.
const SIMPLE_PREVIEW = true;

export default function SelfWorkoutModal({ open, onClose, onSuccess, user, onOpenSupport, onOpenPayments }) {
  useBodyScrollLock(open);
  const [phase, setPhase] = useState("form");   // "form" | "summary" | "generating" | "preview" | "success"
  const [formStepIdx, setFormStepIdx] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [freshUser, setFreshUser] = useState(null);
  const [usageData, setUsageData] = useState(null);   // {can_generate, next_allowed_at}
  const [discardWarning, setDiscardWarning] = useState(false);
  const [hasAdjusted, setHasAdjusted] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState("forward"); // "forward" | "backward"
  const [genMsgIdx, setGenMsgIdx] = useState(0);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open) { fetchedRef.current = false; return; }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    api.get("/users/me").then(r => setFreshUser(r.data)).catch(() => {});
    api.get("/ai/self-workout/last-usage").then(r => setUsageData(r.data)).catch(() => {});
  }, [open]);

  // Rotaciona mensagens durante a fase de geração
  useEffect(() => {
    if (phase !== "generating") return;
    setGenMsgIdx(0);
    const iv = setInterval(() => setGenMsgIdx(i => Math.min(i + 1, GENERATING_MESSAGES.length - 1)), 5000);
    return () => clearInterval(iv);
  }, [phase]);

  // Guarda contra fechar a aba / F5 DURANTE a geração: se o navegador cair no
  // meio da chamada da IA, o treino pode não ser gravado. Dispara o aviso nativo
  // "Sair do site?". Só ativo na fase de geração.
  useEffect(() => {
    if (phase !== "generating") return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";  // exigido por Chrome/Firefox pra exibir o prompt
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase]);

  const profile = freshUser ?? user;

  // Idade: usa a data já salva no perfil, ou a que está sendo digitada agora nesta sessão
  const effectiveBirthDate = profile?.birth_date || form.birth_date || null;
  const currentAge   = calculateAge(effectiveBirthDate);
  const isUnderAge   = currentAge != null && currentAge < MIN_SELF_SERVICE_AGE;
  // Bloqueio duro: só quando a data já vem confirmada do perfil (não a meio-digitar no form)
  const showAgeBlock = Boolean(profile?.birth_date) && isUnderAge;

  const needsBirthDate     = !profile?.birth_date;
  const needsGender        = !profile?.gender;
  const needsHeightCm      = !profile?.height_cm;
  const needsActivityLevel = !profile?.activity_level;
  const hasProfileMissing  = needsBirthDate || needsGender || needsHeightCm; // sem activity

  // Consentimento sempre primeiro. Atividade em step próprio para não sobrecarregar o perfil.
  const FORM_STEPS = useMemo(() => {
    const steps = ["termos"];
    if (hasProfileMissing)  steps.push("perfil");
    if (needsActivityLevel) steps.push("atividade");
    steps.push("objetivos", "habitos", "cardio", "personal");
    return steps;
  }, [hasProfileMissing, needsActivityLevel]);

  // FORM_STEPS pode mudar de tamanho quando freshUser chega (perfil completo/incompleto
  // só é conhecido após o fetch) - evita ficar apontando para um índice fora dos limites.
  useEffect(() => {
    setFormStepIdx(idx => Math.min(idx, FORM_STEPS.length - 1));
  }, [FORM_STEPS]);

  const currentStepKey = FORM_STEPS[formStepIdx];
  const isLastFormStep = formStepIdx === FORM_STEPS.length - 1;

  function reset() {
    setPhase("form");
    setFormStepIdx(0);
    setAgreed(false);
    setForm(DEFAULT_FORM);
    setError("");
    setPreview(null);
    setConfirming(false);
    setSuccessData(null);
    setUsageData(null);
  }

  function handleClose() {
    if (phase === "generating" || confirming) return;
    // Se já gerou (preview existe) e ainda não confirmou, bloquear saída
    // independente do phase atual (pode ter voltado ao formulário via Ajustar)
    if (preview !== null && phase !== "success") { setDiscardWarning(true); return; }
    reset();
    onClose();
  }


  // Per-step validation
  const stepValid = useMemo(() => {
    if (currentStepKey === "termos")    return agreed;
    if (currentStepKey === "perfil") {
      const weightOk = form.weight_kg !== "" && Number(form.weight_kg) > 0;
      // Data válida: todos os 3 parts preenchidos (form.birth_date só é set quando todos ok)
      const birthDateValid = /^\d{4}-\d{2}-\d{2}$/.test(form.birth_date) &&
        !isNaN(new Date(form.birth_date).getTime());
      const birthOk  = !needsBirthDate || (birthDateValid && !isUnderAge);
      const genderOk = !needsGender    || form.gender !== "";
      const heightOk = !needsHeightCm  || (form.height_cm !== "" && Number(form.height_cm) > 0);
      return weightOk && birthOk && genderOk && heightOk;
    }
    if (currentStepKey === "atividade") return form.activity_level !== "";
    if (currentStepKey === "objetivos") return form.goals.length > 0;
    if (currentStepKey === "habitos")   return form.days_per_week > 0 && form.session_duration_minutes > 0;
    if (currentStepKey === "cardio")   return true; // foco e cardio são opcionais
    if (currentStepKey === "personal") return true; // personalização é opcional
    return true;
  }, [currentStepKey, agreed, form, needsBirthDate, needsGender, needsHeightCm, isUnderAge]);

  function profilePayload() {
    return {
      birth_date:     needsBirthDate     ? (form.birth_date     || null) : null,
      gender:         needsGender        ? (form.gender         || null) : null,
      height_cm:      needsHeightCm      ? (Number(form.height_cm) || null) : null,
      weight_kg:      Number(form.weight_kg) || null,
      activity_level: needsActivityLevel ? (form.activity_level || null) : null,
      days_per_week:  form.days_per_week || null,
    };
  }

  async function handleGenerate() {
    setError("");
    setPhase("generating");

    const effectiveActivity = profile?.activity_level || form.activity_level || "";
    const body = {
      goals: form.goals,
      days_per_week: form.days_per_week,
      session_duration_minutes: form.session_duration_minutes ?? undefined,
      fitness_level: deriveFitnessLevel(effectiveActivity),
      restrictions: form.restrictions || null,
      focus: form.focus || null,
      secondary_goals: form.secondary_goals,
      include_cardio: form.include_cardio,
      cardio_type: form.cardio_type || undefined,
      cardio_duration_minutes: form.cardio_duration_minutes ?? undefined,
      about_me: form.about_me || null,
      equipment: form.equipment,
      ...profilePayload(),
    };

    try {
      const res = await api.post("/ai/self-workout/preview", body);
      const data = res.data;
      setPreview({
        days: data.days,
        rationale: data.rationale,
        rationale_student: data.rationale_student || null,
        ai_usage_log_id: data.ai_usage_log_id ?? null,
        gender: profile?.gender || form.gender || null,
        age: calculateAge(profile?.birth_date),
      });
      setPhase("preview");
    } catch (err) {
      const _detail = err?.response?.data?.detail;
      if (typeof _detail === "object" && _detail?.error === "renovation_limit_reached") {
        setError(_detail.message || "Limite de renovações atingido.");
        setPhase("renovation-limit");
      } else {
        // Erros com detalhe em objeto (daily_generation_cap, frequency_too_high_for_level,
        // cooldown_active) trazem .message — antes caíam em string vazia.
        const _msg = typeof _detail === "string" ? _detail : (_detail?.message || "");
        setError(_msg);
        setPhase("gen-error");
      }
    }
  }

  async function handleConfirm() {
    if (!preview?.days) return;
    setError("");
    setConfirming(true);
    try {
      const res = await api.post("/ai/self-workout/confirm", {
        days: preview.days,
        rationale: preview.rationale || null,
        rationale_student: preview.rationale_student || null,
        ai_usage_log_id: preview.ai_usage_log_id ?? null,
        ...profilePayload(),
      });
      // Busca perfil atualizado para sincronizar estado do usuário
      let freshProfile = null;
      try { freshProfile = (await api.get("/users/me")).data; } catch (_) {}
      setSuccessData({ ...res.data, freshProfile });
      setPhase("success");
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar treino. Tente novamente.");
    } finally {
      setConfirming(false);
    }
  }

  function handleNext() {
    setTransitionDirection("forward");
    if (isLastFormStep) {
      setPhase("summary");
    } else {
      setFormStepIdx(i => i + 1);
    }
  }

  function handleBack() {
    setTransitionDirection("backward");
    if (formStepIdx > 0) setFormStepIdx(i => i - 1);
  }

  if (!open) return null;

  // Bloqueio de limite de renovações (Free plan)
  if (usageData && usageData.has_reached_renovation_limit) {
    const renovationLimitContent = (
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={e => e.stopPropagation()}>
          <ModalHeader onClose={handleClose} disabled={false} />
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIcon}>
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div>
                <h2 className={styles.title}>Limite de renovações atingido</h2>
                <p className={styles.sub}>Faça upgrade para continuar</p>
              </div>
            </div>
          </div>
          <div className={styles.body}>
            <div className={styles.generatingWrap}>
              <p className={styles.generatingText}>
                Você já usou suas renovações de treino disponíveis no plano Free.
              </p>
              <div className={styles.featureList} style={{ marginTop: "16px" }}>
                <div className={`${styles.featureItem} ${styles.featureItemActive}`}>
                  <FontAwesomeIcon icon={faCheck} className={styles.featureIcon} />
                  <span>Uma geração inicial</span>
                </div>
                <div className={`${styles.featureItem} ${styles.featureItemActive}`}>
                  <FontAwesomeIcon icon={faCheck} className={styles.featureIcon} />
                  <span>Uma renovação após 90 dias</span>
                </div>
                <div className={`${styles.featureItem} ${styles.featureItemInactive}`}>
                  <FontAwesomeIcon icon={faXmark} className={styles.featureIcon} />
                  <span>Próximas renovações</span>
                </div>
              </div>
              <p className={styles.generatingHint}>
                Faça upgrade para o <strong>Plus AI</strong> e desbloqueie renovações ilimitadas,
                análise de progresso, dieta personalizada e muito mais.
              </p>
            </div>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.btnSecondary} onClick={handleClose}>
              Talvez depois
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => {
                handleClose();
                if (onOpenPayments) setTimeout(() => onOpenPayments(), 200);
              }}
            >
              <FontAwesomeIcon icon={faStar} /> Upgrade para Plus AI
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(renovationLimitContent, document.body);
  }

  // Bloqueio de cooldown: aluno já gerou treino nos últimos 90 dias
  if (usageData && !usageData.can_generate) {
    const nextDate = usageData.next_allowed_at
      ? new Date(usageData.next_allowed_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
      : null;
        const cooldownContent = (
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={e => e.stopPropagation()}>
          <ModalHeader onClose={handleClose} disabled={false} />
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIcon}>
                <FontAwesomeIcon icon={faLock} />
              </div>
              <div>
                <h2 className={styles.title}>Treino já gerado</h2>
                <p className={styles.sub}>Próxima geração disponível em breve</p>
              </div>
            </div>
          </div>
          <div className={styles.body}>
            <div className={styles.generatingWrap}>
              <p className={styles.generatingText}>
                Você já usou sua geração de treino com IA. Para manter a qualidade e a progressão,
                permitimos <strong>1 geração a cada 3 meses</strong>.
              </p>
              {nextDate && (
                <p className={styles.generatingHint}>
                  Sua próxima geração estará disponível a partir de{" "}
                  <strong>{nextDate}</strong>.
                </p>
              )}
              <p className={styles.generatingHint}>
                Enquanto isso, siga o treino atual - a consistência é o que gera resultado.
                Se precisar de ajustes, peça orientação a um professor.
              </p>
            </div>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.btnPrimary} onClick={handleClose}>
              Entendi
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(cooldownContent, document.body);
  }

  // Bloqueio de limite de renovações no plano Free
  if (phase === "renovation-limit") {
    const renovationLimitContent = (
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={e => e.stopPropagation()}>
          <ModalHeader onClose={handleClose} disabled={false} />
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIcon}>
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div>
                <h2 className={styles.title}>Limite de renovações atingido</h2>
                <p className={styles.sub}>Faça upgrade para continuar</p>
              </div>
            </div>
          </div>
          <div className={styles.body}>
            <div className={styles.generatingWrap}>
              <p className={styles.generatingText}>
                Você já usou suas renovações de treino disponíveis no plano Free.
              </p>
              <div className={styles.featureList} style={{ marginTop: "16px" }}>
                <div className={`${styles.featureItem} ${styles.featureItemActive}`}>
                  <FontAwesomeIcon icon={faCheck} className={styles.featureIcon} />
                  <span>Uma geração inicial</span>
                </div>
                <div className={`${styles.featureItem} ${styles.featureItemActive}`}>
                  <FontAwesomeIcon icon={faCheck} className={styles.featureIcon} />
                  <span>Uma renovação após 90 dias</span>
                </div>
                <div className={`${styles.featureItem} ${styles.featureItemInactive}`}>
                  <FontAwesomeIcon icon={faXmark} className={styles.featureIcon} />
                  <span>Próximas renovações</span>
                </div>
              </div>
              <p className={styles.generatingHint}>
                Faça upgrade para o <strong>Plus AI</strong> e desbloqueie renovações ilimitadas,
                análise de progresso, dieta personalizada e muito mais.
              </p>
            </div>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.btnSecondary} onClick={handleClose}>
              Talvez depois
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => {
                handleClose();
                if (onOpenPayments) setTimeout(() => onOpenPayments(), 200);
              }}
            >
              <FontAwesomeIcon icon={faStar} /> Upgrade para Plus AI
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(renovationLimitContent, document.body);
  }

  // Bloqueio pra menores de idade confirmados no perfil (LGPD Art. 14 - self-service
  // exige acompanhamento de um adulto responsável; menores usam via professor)
  if (showAgeBlock) {
    const blockedContent = (
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={e => e.stopPropagation()}>
          <ModalHeader onClose={handleClose} disabled={false} />
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIcon}>
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div>
                <h2 className={styles.title}>Self-service disponível pra maiores de 18</h2>
                <p className={styles.sub}>Geração de treino sem professor</p>
              </div>
            </div>
          </div>
          <div className={styles.body}>
            <div className={styles.generatingWrap}>
              <p className={styles.generatingText}>
                Por enquanto, gerar treino com IA sozinho é liberado só pra maiores de 18 anos.
              </p>
              <p className={styles.generatingHint}>
                Peça pra um professor vincular sua conta - assim você tem acesso a treino
                com acompanhamento humano e IA, com um responsável por trás.
              </p>
            </div>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.btnPrimary} onClick={handleClose}>
              Entendi
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(blockedContent, document.body);
  }

  // ── Header title logic ─────────────────────────────────────────────────────
  const meta = phase === "form" ? STEP_META[currentStepKey] : null;
  const headerTitle =
    phase === "summary"    ? "Confirmar dados"        :
    phase === "preview"    ? (SIMPLE_PREVIEW ? "Treino pronto!" : "Prévia do seu treino") :
    phase === "success"    ? "Treino criado!"        :
    phase === "generating" ? "Gerando seu treino…"   :
    meta?.title ?? "";
  const headerSub =
    phase === "summary"    ? "Revise as informações antes de gerar" :
    phase === "preview"    ? (SIMPLE_PREVIEW ? "É só conferir e abrir seus treinos" : "Revise antes de confirmar") :
    phase === "success"    ? "Seus treinos estão prontos" :
    phase === "generating" ? "Montando sua ficha personalizada" :
    meta?.sub ?? "";

  const modalContent = (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        <ModalHeader onClose={handleClose} disabled={phase === "generating" || confirming} />

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <FontAwesomeIcon icon={phase === "form" ? (meta?.icon ?? faWandMagicSparkles) : faWandMagicSparkles} />
            </div>
            <div>
              <h2 className={styles.title}>{headerTitle}</h2>
              <p className={styles.sub}>{headerSub}</p>
            </div>
          </div>
        </div>

        {/* Progress dots (form phase only) */}
        {phase === "form" && (
          <div
            className={styles.stepDots}
            role="progressbar"
            aria-valuenow={formStepIdx + 1}
            aria-valuemin={1}
            aria-valuemax={FORM_STEPS.length}
            aria-label={`Passo ${formStepIdx + 1} de ${FORM_STEPS.length}`}
          >
            <div className={styles.stepDotsTrack}>
              {FORM_STEPS.map((_, i) => (
                <div key={i} className={`${styles.stepDot} ${i <= formStepIdx ? styles.stepDotActive : ""}`} />
              ))}
            </div>
            <div className={styles.stepProgressRow}>
              <span className={styles.stepProgressText} aria-hidden="true">
                Passo {formStepIdx + 1} de {FORM_STEPS.length}
              </span>
              {formStepIdx < FORM_STEPS.length - 1 && (
                <span className={styles.nextStepHint}>
                  A seguir: {STEP_META[FORM_STEPS[formStepIdx + 1]]?.name}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── SUMMARY ── */}
        {phase === "summary" && (
          <div className={styles.body}>
            <div className={`${styles.summaryGrid} ${transitionDirection === "backward" ? styles.formGridBack : ""}`}>

              <div className={styles.summarySection}>
                <span className={styles.summarySectionTitle}>
                  <FontAwesomeIcon icon={faUser} /> Perfil
                </span>
                <div className={styles.summaryItems}>
                  {(profile?.weight_kg || form.weight_kg) && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Peso</span>
                      <span className={styles.summaryVal}>{profile?.weight_kg || form.weight_kg} kg</span>
                    </div>
                  )}
                  {(profile?.height_cm || form.height_cm) && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Altura</span>
                      <span className={styles.summaryVal}>{profile?.height_cm || form.height_cm} cm</span>
                    </div>
                  )}
                  {(profile?.birth_date || form.birth_date) && calculateAge(profile?.birth_date || form.birth_date) && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Idade</span>
                      <span className={styles.summaryVal}>{calculateAge(profile?.birth_date || form.birth_date)} anos</span>
                    </div>
                  )}
                  {(profile?.gender || form.gender) && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Gênero</span>
                      <span className={styles.summaryVal} style={{ textTransform: "capitalize" }}>
                        {(profile?.gender || form.gender).replace("_", " ")}
                      </span>
                    </div>
                  )}
                  {(profile?.activity_level || form.activity_level) && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Atividade</span>
                      <span className={styles.summaryVal} style={{ textTransform: "capitalize" }}>
                        {(profile?.activity_level || form.activity_level).replace("_", " ")}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.summarySection}>
                <span className={styles.summarySectionTitle}>
                  <FontAwesomeIcon icon={faBullseye} /> Objetivos
                </span>
                <div className={styles.summaryItems}>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Principal</span>
                    <span className={styles.summaryVal} style={{ textTransform: "capitalize" }}>{form.goals[0]}</span>
                  </div>
                  {form.secondary_goals.length > 0 && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Adicionais</span>
                      <span className={styles.summaryVal}>{form.secondary_goals.join(", ")}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.summarySection}>
                <span className={styles.summarySectionTitle}>
                  <FontAwesomeIcon icon={faCalendarDays} /> Rotina
                </span>
                <div className={styles.summaryItems}>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Frequência</span>
                    <span className={styles.summaryVal}>{form.days_per_week}× por semana</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Duração</span>
                    <span className={styles.summaryVal}>{form.session_duration_minutes} min / sessão</span>
                  </div>
                  {form.focus && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Foco</span>
                      <span className={styles.summaryVal} style={{ textTransform: "capitalize" }}>{form.focus}</span>
                    </div>
                  )}
                  {form.include_cardio && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Cardio</span>
                      <span className={styles.summaryVal}>{form.cardio_type} · {form.cardio_duration_minutes} min</span>
                    </div>
                  )}
                </div>
              </div>

              {(form.restrictions || form.about_me) && (
                <div className={styles.summarySection}>
                  <span className={styles.summarySectionTitle}>
                    <FontAwesomeIcon icon={faStar} /> Personalização
                  </span>
                  <div className={styles.summaryItems}>
                    {form.restrictions && (
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryKey}>Restrições</span>
                        <span className={styles.summaryVal}>{form.restrictions}</span>
                      </div>
                    )}
                    {form.about_me && (
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryKey}>Sobre você</span>
                        <span className={styles.summaryVal}>{form.about_me}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>


              {/* Alerta: dados de personalização incompletos */}
              {(!form.restrictions && !form.about_me) && (
                <div style={{
                  backgroundColor: "rgba(239, 159, 39, 0.12)",
                  border: "1px solid rgba(239, 159, 39, 0.3)",
                  borderRadius: 12,
                  padding: "14px 16px",
                  marginTop: 16,
                  marginBottom: 16,
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                }}>
                  <FontAwesomeIcon 
                    icon={faCircleExclamation} 
                    style={{ color: "var(--feedback-warning)", marginTop: 2, flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, fontSize: 14, color: "var(--feedback-warning)", fontWeight: 600 }}>
                    <div style={{ marginBottom: 4 }}>Seus dados de personalização são muito importantes!</div>
                    <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.9 }}>
                      Informar suas <strong>restrições/lesões</strong> e <strong>preferências</strong> ajuda a IA a criar um treino realmente personalizado para você - não genérico.
                    </div>
                  </div>
                </div>
              )}
              {(form.restrictions && !form.about_me) && (
                <div style={{
                  backgroundColor: "rgba(239, 159, 39, 0.08)",
                  border: "1px solid rgba(239, 159, 39, 0.2)",
                  borderRadius: 12,
                  padding: "12px 14px",
                  marginTop: 16,
                  marginBottom: 16,
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  fontSize: 12,
                  color: "var(--text-0-60)",
                }}>
                  <FontAwesomeIcon 
                    icon={faCircleExclamation} 
                    style={{ color: "var(--feedback-warning)", marginTop: 2, flexShrink: 0 }}
                  />
                  <span>Compartilhe suas <strong>preferências</strong> e <strong>rotina</strong> para personalizar ainda mais seu treino.</span>
                </div>
              )}
              {(!form.restrictions && form.about_me) && (
                <div style={{
                  backgroundColor: "rgba(239, 159, 39, 0.08)",
                  border: "1px solid rgba(239, 159, 39, 0.2)",
                  borderRadius: 12,
                  padding: "12px 14px",
                  marginBottom: 16,
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  fontSize: 12,
                  color: "var(--text-0-60)",
                }}>
                  <FontAwesomeIcon 
                    icon={faCircleExclamation} 
                    style={{ color: "var(--feedback-warning)", marginTop: 2, flexShrink: 0 }}
                  />
                  <span>Não esqueça de informar suas <strong>restrições/lesões</strong> para segurança.</span>
                </div>
              )}

            <div className={styles.footer}>
              <button type="button" className={styles.btnSecondary}
                onClick={() => { setPhase("form"); setFormStepIdx(FORM_STEPS.length - 1); }}>
                <FontAwesomeIcon icon={faPenToSquare} /> Editar
              </button>
              <button type="button" className={styles.btnPrimary} onClick={handleGenerate}>
                <FontAwesomeIcon icon={faWandMagicSparkles} /> Gerar meu treino
              </button>
            </div>
          </div>
        )}

        {/* ── GENERATING ── */}
        {phase === "generating" && (
          <div className={styles.body}>
            <div className={styles.generatingWrap}>
              <FontAwesomeIcon icon={faSpinner} spin className={styles.generatingIcon} />
              <p className={styles.generatingText} key={genMsgIdx}>
                {GENERATING_MESSAGES[genMsgIdx]}
              </p>
              <p className={styles.generatingHint}>Personalizando cada detalhe para você</p>

              {/* Destaque: não fechar a janela durante a geração — se o navegador
                  cai no meio da chamada da IA, o treino pode não ser gravado. */}
              <div
                role="alert"
                style={{
                  marginTop: 22,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  borderRadius: 14,
                  background: "rgba(245, 158, 11, 0.14)",
                  border: "1.5px solid rgba(245, 158, 11, 0.55)",
                  color: "var(--text)",
                  maxWidth: 420,
                  margin: "22px auto 0",
                }}
              >
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  style={{ color: "#f59e0b", fontSize: 22, flexShrink: 0 }}
                />
                <span style={{ fontSize: 14.5, lineHeight: 1.45, textAlign: "left" }}>
                  <strong>Não feche esta janela</strong> enquanto geramos seu treino.
                  Leva só alguns segundos e ele já fica salvo pra você.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── FORM ── */}
        {phase === "form" && (
          <div className={styles.body}>

            {/* Step: Termos - welcome-first */}
            {currentStepKey === "termos" && (
              <div className={`${styles.formGrid} ${transitionDirection === "backward" ? styles.formGridBack : ""}`}>
                {/* Value prop - promessa antes do formulário */}
                <div className={styles.welcomeHero}>
                  <h3 className={styles.welcomeTitle}>Seu treino, do jeito certo.</h3>
                  <p className={styles.welcomeText}>
                    Em menos de 2 minutos a IA monta uma ficha completa - divisão semanal,
                    exercícios, séries e cargas - personalizada pro seu corpo e seus objetivos.
                  </p>
                </div>

                {/* Consentimento compacto */}
                <div className={styles.consentCompact}>
                  <div className={styles.consentCompactHeader}>
                    <FontAwesomeIcon icon={faShieldHalved} className={styles.consentCompactIcon} />
                    <span>Uso de dados pela IA</span>
                  </div>
                  <p className={styles.consentCompactText}>
                    Usamos peso, altura, idade e objetivos para personalizar seu treino.
                    Dados processados com confidencialidade - nunca vendidos.{" "}
                    <a href="https://treinonexo.com.br/privacy.html" target="_blank" rel="noopener noreferrer" className={styles.consentLink}>
                      Política de Privacidade
                    </a>.
                  </p>
                </div>

                <label className={styles.consentCheck}>
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    className={styles.consentCheckInput}
                  />
                  <span>Concordo com o uso dos meus dados para geração do treino personalizado.</span>
                </label>
              </div>
            )}

            {/* Step: Perfil */}
            {currentStepKey === "perfil" && (
              <div className={`${styles.formGrid} ${transitionDirection === "backward" ? styles.formGridBack : ""}`}>
                <div className={styles.stepIntro}>
                  Precisamos de algumas informações básicas para montar um treino adequado para você.
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>
                    Peso atual (kg) <span className={styles.required}>*</span>
                  </label>
                  <input style={fieldStyle} type="number" min="30" max="250" step="0.1"
                    placeholder="Ex: 75"
                    value={form.weight_kg}
                    onChange={e => setForm(f => ({ ...f, weight_kg: e.target.value }))} />
                </div>

                {needsHeightCm && (
                  <div className={styles.field}>
                    <label className={styles.label}>
                      Altura (cm) <span className={styles.required}>*</span>
                    </label>
                    <input style={fieldStyle} type="number" min="100" max="250" step="1"
                      placeholder="Ex: 175"
                      value={form.height_cm}
                      onChange={e => setForm(f => ({ ...f, height_cm: e.target.value }))} />
                  </div>
                )}

                {needsBirthDate && (
                  <div className={styles.field}>
                    <label className={styles.label}>
                      Data de nascimento <span className={styles.required}>*</span>
                    </label>
                    {/* 3 inputs numéricos (estilo validade de cartão) — digitação
                        direta em vez de rolar uma lista de 100+ anos num select/picker */}
                    <SplitDateInput
                      value={form.birth_date}
                      onChange={v => setForm(f => ({ ...f, birth_date: v }))}
                      inputStyle={fieldStyle}
                      maxDate={todayLocalISO()}
                    />
                    {/^\d{4}-\d{2}-\d{2}$/.test(form.birth_date) && isUnderAge && (
                      <div className={styles.error}>
                        O self-service com IA é liberado só pra maiores de 18 anos. Peça pra um
                        professor vincular sua conta pra ter acesso à plataforma.
                      </div>
                    )}
                  </div>
                )}

                {needsGender && (
                  <div className={styles.field}>
                    <label className={styles.label}>
                      Gênero <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.optionGrid}>
                      {[
                        { v: "masculino",            l: "Masculino" },
                        { v: "feminino",             l: "Feminino" },
                        { v: "outro",                l: "Outro" },
                      ].map(({ v, l }) => (
                        <OptionCard key={v} label={l} active={form.gender === v}
                          onClick={() => setForm(f => ({ ...f, gender: v }))} />
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Step: Atividade */}
            {currentStepKey === "atividade" && (
              <div className={`${styles.formGrid} ${transitionDirection === "backward" ? styles.formGridBack : ""}`}>
                <div className={styles.stepIntro}>
                  Qual é o seu nível de atividade física <strong>hoje</strong>? Seja honesto - isso calibra a intensidade do seu treino.
                </div>
                <div className={styles.optionList}>
                  {[
                    { v: "sedentario",  l: "Sedentário",  sub: "Pouco ou nenhum exercício",      icon: faBed           },
                    { v: "leve",        l: "Leve",        sub: "1–2× por semana",                icon: faPersonWalking },
                    { v: "moderado",    l: "Moderado",    sub: "3–4× por semana",                icon: faPersonRunning },
                    { v: "ativo",       l: "Ativo",       sub: "5–6× por semana",                icon: faDumbbell      },
                    { v: "muito_ativo", l: "Muito ativo", sub: "Diariamente / alta intensidade", icon: faBolt          },
                  ].map(({ v, l, sub, icon }) => (
                    <OptionCard key={v} label={l} sub={sub} icon={icon} active={form.activity_level === v}
                      onClick={() => {
                        setForm(f => ({ ...f, activity_level: v }));
                        setTimeout(() => handleNext(), 220);
                      }} />
                  ))}
                </div>
              </div>
            )}

            {/* Step: Objetivos */}
            {currentStepKey === "objetivos" && (
              <div className={`${styles.formGrid} ${transitionDirection === "backward" ? styles.formGridBack : ""}`}>
                <div className={styles.stepIntro}>
                  O que você quer priorizar no seu treino?
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Objetivo principal</label>
                  <div className={styles.optionList}>
                    {[
                      { v: "hipertrofia",     l: "Hipertrofia",      sub: "Ganhar massa muscular",      icon: faArrowTrendUp  },
                      { v: "força",           l: "Força",            sub: "Aumentar carga e potência",  icon: faWeightHanging },
                      { v: "emagrecimento",   l: "Emagrecimento",    sub: "Queima de gordura",          icon: faFire          },
                      { v: "condicionamento", l: "Condicionamento",  sub: "Resistência e saúde geral",  icon: faHeartPulse    },
                      { v: "reabilitação",    l: "Reabilitação",     sub: "Recuperação e prevenção",    icon: faBandage       },
                    ].map(({ v, l, sub, icon }) => (
                      <OptionCard key={v} label={l} sub={sub} icon={icon} active={form.goals[0] === v}
                        onClick={() => setForm(f => ({ ...f, goals: [v] }))} />
                    ))}
                  </div>
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>
                    Objetivos adicionais <span className={styles.optional}>(opcional)</span>
                  </label>
                  <div className={styles.chips}>
                    {SECONDARY_GOALS.map(goal => (
                      <Chip key={goal} label={goal} active={form.secondary_goals.includes(goal)}
                        onClick={() => setForm(f => ({
                          ...f,
                          secondary_goals: f.secondary_goals.includes(goal)
                            ? f.secondary_goals.filter(g => g !== goal)
                            : [...f.secondary_goals, goal],
                        }))} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step: Hábitos */}
            {currentStepKey === "habitos" && (
              <div className={`${styles.formGrid} ${transitionDirection === "backward" ? styles.formGridBack : ""}`}>
                <div className={styles.stepIntro}>
                  Como você quer organizar sua rotina de treinos?
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Dias por semana</label>
                  <div className={styles.optionGrid}>
                    {[2, 3, 4, 5, 6].map(n => (
                      <OptionCard key={n} label={`${n}×`} sub={n <= 3 ? "Leve" : n === 4 ? "Moderado" : "Intenso"}
                        active={form.days_per_week === n}
                        onClick={() => setForm(f => ({ ...f, days_per_week: n }))} />
                    ))}
                  </div>
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Duração de cada sessão</label>
                  <div className={styles.optionGrid}>
                    {[
                      { v: 45, l: "45 min", sub: "Treino curto" },
                      { v: 60, l: "60 min", sub: "Treino padrão" },
                      { v: 75, l: "75 min", sub: "Treino longo" },
                      { v: 90, l: "90 min", sub: "Treino completo" },
                    ].map(({ v, l, sub }) => (
                      <OptionCard key={v} label={l} sub={sub}
                        active={form.session_duration_minutes === v}
                        onClick={() => setForm(f => ({ ...f, session_duration_minutes: v }))} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step: Cardio e Foco */}
            {currentStepKey === "cardio" && (
              <div className={`${styles.formGrid} ${transitionDirection === "backward" ? styles.formGridBack : ""}`}>
                <div className={styles.stepIntro}>
                  Customize seu treino com foco muscular e cardio
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>
                    Foco muscular <span className={styles.optional}></span>
                  </label>
                  <div className={styles.optionGrid}>
                    {[
                      { v: "",            l: "Equilibrado" },
                      { v: "superiores",  l: "Superiores" },
                      { v: "inferiores",  l: "Inferiores" },
                      { v: "glúteos",     l: "Glúteos" },
                      { v: "core",        l: "Core" },
                      { v: "lombar",      l: "Lombar" },
                      { v: "obliquos",    l: "Oblíquos" },
                    ].map(({ v, l }) => (
                      <OptionCard key={v} label={l} active={form.focus === v}
                        onClick={() => setForm(f => ({ ...f, focus: v }))} />
                    ))}
                  </div>
                </div>

                <div className={styles.field}>
                  <div className={styles.toggleRow}>
                    <button type="button" className={styles.toggle}
                      onClick={() => setForm(f => ({ ...f, include_cardio: !f.include_cardio }))}
                      aria-pressed={form.include_cardio}>
                      <span className={`${styles.toggleThumb} ${form.include_cardio ? styles.toggleThumbOn : ""}`} />
                    </button>
                    <label className={styles.label} style={{ margin: 0, cursor: "pointer" }}
                      onClick={() => setForm(f => ({ ...f, include_cardio: !f.include_cardio }))}>
                      Incluir cardio ao final de cada sessão
                    </label>
                  </div>
                  {form.include_cardio && (
                    <div className={styles.fieldRow} style={{ marginTop: 12 }}>
                      <div className={styles.field}>
                        <label className={styles.label}>Tipo</label>
                        <select style={fieldStyle} value={form.cardio_type}
                          onChange={e => setForm(f => ({ ...f, cardio_type: e.target.value }))}>
                          <option value="">Escolha o tipo de cardio</option>
                          <option value="livre">Livre</option>
                          <option value="Esteira">Esteira</option>
                          <option value="Bicicleta">Bicicleta</option>
                          <option value="Elíptico">Elíptico</option>
                        </select>
                      </div>
                      <div className={styles.field}>
                        <label className={styles.label}>Duração</label>
                        <select style={fieldStyle} value={form.cardio_duration_minutes}
                          onChange={e => setForm(f => ({ ...f, cardio_duration_minutes: Number(e.target.value) }))}>
                          <option value="">Escolha a duração</option>
                          {[10, 15, 20, 30].map(n => <option key={n} value={n}>{n} min</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step: Personalização */}
            {currentStepKey === "personal" && (
              <div className={`${styles.formGrid} ${transitionDirection === "backward" ? styles.formGridBack : ""}`}>
                <div className={styles.stepIntro}>
                  Esse é o passo que separa um treino genérico do <em>seu</em> treino.
                  Não precisa ser muito - qualquer detalhe ajuda a IA a acertar na mosca.
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>
                    Restrições / lesões <span className={styles.optional}>(opcional)</span>
                  </label>
                  <input style={fieldStyle} type="text"
                    placeholder="Ex: dor no joelho, evitar agachamento..."
                    value={form.restrictions}
                    maxLength={RESTRICTIONS_MAX}
                    onChange={e => setForm(f => ({ ...f, restrictions: e.target.value }))} />
                  <span className={`${styles.charCount} ${form.restrictions.length >= RESTRICTIONS_MAX ? styles.charCountLimit : ""}`}>
                    {form.restrictions.length}/{RESTRICTIONS_MAX}
                  </span>
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>
                    Conte mais sobre você <span className={styles.optional}>(opcional)</span>
                  </label>
                  <textarea
                    style={{ ...fieldStyle, resize: "vertical", minHeight: 100, lineHeight: 1.5 }}
                    placeholder="Quanto mais você contar, mais o treino fica com a sua cara. Pode falar de tudo: exercícios que você ama ou odeia, se quer mais ou menos séries, grupos musculares que quer priorizar, dias que não rolam pra treinar, o equipamento que você tem, quanto tempo quer gastar por treino... Ex: Curto treino puxado, quero mais séries nos exercícios. Adoro terra e agachamento, foco em costas. Não posso treinar às sextas."
                    value={form.about_me}
                    maxLength={ABOUT_ME_MAX}
                    onChange={e => setForm(f => ({ ...f, about_me: e.target.value }))}
                    rows={5}
                  />
                  <span className={`${styles.charCount} ${form.about_me.length >= ABOUT_ME_MAX ? styles.charCountLimit : ""}`}>
                    {form.about_me.length}/{ABOUT_ME_MAX}
                  </span>
                </div>
              </div>
            )}

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.footer}>
              {formStepIdx > 0 ? (
                <button type="button" className={styles.btnSecondary} onClick={handleBack}>
                  ← Voltar
                </button>
              ) : (
                <button type="button" className={styles.btnSecondary} onClick={handleClose} disabled={phase === "generating" || confirming}>
                  Cancelar
                </button>
              )}
              <button type="button" className={styles.btnPrimary}
                onClick={handleNext}
                disabled={!stepValid}>
                {isLastFormStep
                  ? <><FontAwesomeIcon icon={faWandMagicSparkles} /> Gerar meu treino</>
                  : "Continuar →"
                }
              </button>
            </div>
          </div>
        )}

        {/* ── GEN-ERROR ── */}
        {phase === "gen-error" && (
          <div className={styles.body}>
            <div className={styles.genError}>
              <div className={styles.genErrorIcon}>
                <FontAwesomeIcon icon={faCircleExclamation} />
              </div>
              <h3 className={styles.genErrorTitle}>Não conseguimos montar seu treino</h3>
              <p className={styles.genErrorMsg}>
                A combinação de preferências que você escolheu não se encaixou no nosso catálogo desta vez.
                Isso pode acontecer com combinações muito específicas - tente ajustar o foco ou os dias de treino.
              </p>
              {error && (
                <p className={styles.genErrorDetail}>{error}</p>
              )}
              <div className={styles.genErrorBtns}>
                <button
                  className={styles.genErrorRetry}
                  onClick={() => { setError(""); setPhase("form"); }}
                >
                  <FontAwesomeIcon icon={faRotateLeft} /> Tentar novamente
                </button>
                {onOpenSupport && (
                  <button
                    className={styles.genErrorSupport}
                    onClick={() => { onClose(); setTimeout(() => onOpenSupport(), 200); }}
                  >
                    <FontAwesomeIcon icon={faCommentDots} /> Dúvidas? Fale conosco
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── PREVIEW ── */}
        {/* ── PREVIEW (SIMPLIFICADO — flag SIMPLE_PREVIEW) ── */}
        {phase === "preview" && preview && SIMPLE_PREVIEW && (
          <div className={styles.body}>
            <div className={styles.previewMeta}>
              <FontAwesomeIcon icon={faDumbbell} className={styles.previewMetaIcon} />
              <span><strong>{preview.days?.length} treino(s)</strong> prontos pra você</span>
            </div>

            {(preview.rationale_student || preview.rationale) && (
              <div className={styles.rationaleBox}>
                <p className={styles.rationaleTitle}>Sobre o seu treino</p>
                <p className={styles.rationaleText}>{preview.rationale_student || preview.rationale}</p>
              </div>
            )}

            <div
              style={{
                marginTop: 16,
                padding: "14px 16px",
                borderRadius: 12,
                background: "rgba(29,158,117,0.10)",
                border: "1px solid rgba(29,158,117,0.30)",
                fontSize: 14,
                lineHeight: 1.5,
                color: "var(--text)",
              }}
            >
              Ficou com alguma dúvida sobre o seu treino? É só acessar o menu{" "}
              <strong>&ldquo;Preciso de ajuda?&rdquo;</strong> - nossa equipe te auxilia.
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.footer}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleConfirm}
                disabled={confirming}
                style={{ width: "100%" }}
              >
                {confirming
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Abrindo...</>
                  : <><FontAwesomeIcon icon={faDumbbell} /> Ver meus treinos</>}
              </button>
            </div>
          </div>
        )}

        {/* ── PREVIEW (COMPLETO — comportamento antigo, quando SIMPLE_PREVIEW=false) ── */}
        {phase === "preview" && preview && !SIMPLE_PREVIEW && (
          <div className={styles.body}>
            <div className={styles.previewMeta}>
              <FontAwesomeIcon icon={faDumbbell} className={styles.previewMetaIcon} />
              <span>
                <strong>{preview.days?.length} treino(s)</strong> gerado(s) pela IA
                {preview.age && ` · ${preview.age} anos`}
                {preview.gender && ` · ${preview.gender}`}
              </span>
            </div>

            {(preview.rationale_student || preview.rationale) && (
              <div className={styles.rationaleBox}>
                <p className={styles.rationaleTitle}>Por que esse treino?</p>
                <p className={styles.rationaleText}>{preview.rationale_student || preview.rationale}</p>
              </div>
            )}

            <div className={styles.dayList}>
              {(preview.days || []).map((day, i) => (
                <DayCard key={i} day={day} index={i} />
              ))}
            </div>
            <div className={styles.previewNote}>
              Ao confirmar, os treinos serão criados na sua conta. Treinos anteriores serão substituídos.
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.footer}>
              <div className={styles.adjustWrapper}>
                <button type="button" className={styles.btnSecondary}
                  onClick={() => { setHasAdjusted(true); setPhase("form"); setError(""); }}
                  disabled={confirming || hasAdjusted}
                  title={hasAdjusted ? "Você já ajustou uma vez" : undefined}>
                  ← Ajustar
                </button>
                {hasAdjusted && (
                  <span className={styles.adjustNote}>
                    Ajuste permitido apenas 1× por geração
                  </span>
                )}
              </div>
              <button type="button" className={styles.btnPrimary} onClick={handleConfirm} disabled={confirming}>
                {confirming
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Salvando...</>
                  : <><FontAwesomeIcon icon={faCheck} /> Confirmar e criar treinos</>
                }
              </button>
            </div>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {phase === "success" && (
          <div className={styles.body}>
            <div className={styles.successWrap}>
              <div className={styles.successIcon}><FontAwesomeIcon icon={faCheck} /></div>
              <p className={styles.successTitle}>{successData?.days_created ?? ""} treino(s) criado(s) com sucesso!</p>
              <p className={styles.successSub}>Seus treinos personalizados já estão disponíveis na sua ficha. Bora treinar!</p>
              <button type="button" className={styles.btnPrimary}
                onClick={() => { onSuccess?.(successData?.freshProfile); reset(); onClose(); }}>
                Ver meus treinos
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ── PREVIEW LOCK WARNING ── */}
      {discardWarning && (
        <div
          style={{position:"fixed",inset:0,zIndex:1200,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}
        >
          <div
            style={{background:"var(--surface-primary,#0f1e36)",borderRadius:14,padding:"2rem 1.75rem",maxWidth:420,width:"100%",textAlign:"center",boxShadow:"0 8px 32px rgba(0,0,0,0.5)"}}
            onClick={e => e.stopPropagation()}
          >
            <div style={{fontSize:"2rem",marginBottom:"0.75rem"}}>🔒</div>
            <p style={{fontWeight:700,fontSize:"1.05rem",marginBottom:"0.75rem",color:"var(--text-primary)"}}>
              Seu treino está esperando por você
            </p>
            <p style={{fontSize:"0.875rem",color:"var(--text-secondary)",marginBottom:"1rem",lineHeight:1.6}}>
              Você já usou sua geração. Confirme o treino antes de fechar - gerações abandonadas não consomem o período de 3 meses, mas você precisará gerar de novo.
            </p>
            <p style={{fontSize:"0.875rem",color:"var(--text-secondary)",marginBottom:"1.5rem",lineHeight:1.6}}>
              Discordou do treino gerado? Use <strong style={{color:"var(--text-primary)"}}>Menu → Precisa de ajuda?</strong> e a equipe TreinoNexo te ajuda.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
              {phase !== "preview" && preview && (
                <button
                  type="button"
                  onClick={() => { setDiscardWarning(false); setPhase("preview"); }}
                  style={{padding:"0.65rem 2rem",borderRadius:8,border:"none",background:"var(--accent,#1d9e75)",color:"#fff",cursor:"pointer",fontWeight:600,fontSize:"0.95rem"}}
                >
                  Ver prévia e confirmar
                </button>
              )}
              <button
                type="button"
                onClick={() => setDiscardWarning(false)}
                style={{padding:"0.65rem 2rem",borderRadius:8,border:"1.5px solid var(--border-0-15)",background:"transparent",color:"var(--text-primary)",cursor:"pointer",fontWeight:600,fontSize:"0.95rem"}}
              >
                Continuar editando
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}